/** @flow */

import React, { useEffect, useState } from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import { route } from "@routes/helper";
import { push } from "connected-react-router";
import { withRouter } from "react-router-dom";
import * as employeesSelectors from "@modules/employee/selectors";
import { Button, SectionComponent, withFocusFirstInput, View, Loader } from "@components";
import { AddEmployeeModal, EmployeeFilter, EmployeeList } from "@modules/employee/components";
import actionCreators from "@modules/employee/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import type { NotificationAuditListType } from "@modules/shared/reducer/employee-notifications/types";
import { getEmployeesNotificationAuditList } from "@modules/shared/selectors";
import { hasSubmitSucceeded } from "redux-form";
import { unpatch } from "@transformers/js-client";
import { getPageRange, getPageRangeOffset } from "@selectors";
import * as Paths from "@routes/constants";
import type { PaginationType, PropertiesSchema } from "@types";
import type { State } from "@store/reducers";
import type { EmployeeType } from "@modules/employee/types";
import type { Client } from "@modules/context/types";
import { getUserId, getUserAllowEmployeeAudit } from "@modules/account/selectors";
import { sortToString } from "@transformers/sort";

import snakecase from "snakecase-keys";

type OwnProps = $Exact<{
  clientId: number,
  canAddEmployee?: boolean,
  selectEmployeeLink: $Keys<typeof Paths>,
}>;

type PropsFromState = $Exact<{
  isLoading: boolean,
  clients: Array<Client>,
  employees: Array<EmployeeType>,
  userId: number | null,
  totalResults: number,
  shownResults: string,
  error: string,
  isFormSubmitted: boolean,
  isAddEmployeeModalVisible: boolean,
  isAddEmployeeChooseClientModalVisible: boolean,
  pagination: PaginationType,
  addEmployeeSchema: ?PropertiesSchema,
  employeeDraft: ?Object,
  employeesNotificationAuditList: NotificationAuditListType,
  allowEmployeeAudit: boolean,
}>;

type PropsFromDispatch = $Exact<{
  addEmployee: typeof actionCreators.AddEmployee,
  getEmployees: typeof actionCreators.GetEmployees,
  onAddEmployeeHandler: () => void,
  onDismissAddEmployeeHandler: () => void,
  getAddEmployeeSchema: typeof actionCreators.GetAddEmployeeSchema,
  updateEmployeeDraft: typeof actionCreators.UpdateEmployeeDraft,
  searchNotificationAuditByStatus: typeof sharedActionCreators.SearchNotificationAuditByStatus,
  push: typeof push,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  location?: {
    state?: any,
  },
}>;

export function EmployeeSearch(props: Props) {
  const auditNeeded = !!props.employeesNotificationAuditList.totalElements;
  const filters = { ...props.location?.state };
  const isLoading = props.isLoading;
  const defaultFilters = { isActive: "true", ...filters };
  const [resultsPage, setResultsPage] = useState(null);
  const [searchFilters, setSearchFilters] = useState(defaultFilters);
  const [searchSort, setSearchSort] = useState([
    { property: "isAbsent", direction: "DESC" },
    { property: "lastName", direction: "ASC" },
  ]);

  useEffect(() => {
    props.allowEmployeeAudit &&
      props.searchNotificationAuditByStatus({
        managerId: props.userId,
        clientId: props.clientId,
        status: ["ONGOING", "PENDING"],
      });

    props.getAddEmployeeSchema({
      clientId: props.clientId,
    });
  }, []);

  useEffect(() => {
    setResultsPage(1);
  }, [searchFilters, searchSort]);

  useEffect(() => {
    props.getEmployees({
      clientId: props.clientId,
      page: resultsPage,
      filters: searchFilters,
      sort: searchSort,
    });
  }, [resultsPage, searchFilters, searchSort]);

  useEffect(() => {
    if (Object.keys(filters).length) {
      setSearchFilters(filters);
    }
  }, [props.location]);

  function handleSubmit(filters?: Object) {
    setSearchFilters(filters || defaultFilters);
  }
  function onSortChange(sort) {
    if (!sort.id) {
      setSearchSort(null);
      return;
    }
    sort.property = sort.id;
    setSearchSort([sort]);
  }
  function onPageChange(page) {
    setResultsPage(page);
  }
  function handleChange(formData: *) {
    props.updateEmployeeDraft(formData);
  }

  function onAddEmployee(formData: *) {
    return props.addEmployee({
      formData: unpatch(snakecase(formData)),
      clientId: props.clientId,
    });
  }

  function reviewMyEmployees() {
    props.push(route("PROFILE_MY_EMPLOYEES"));
  }

  const employeeDraft = {
    ...props.employeeDraft,
    isNonServiceUser: (props.employeeDraft || {}).isNonServiceUser
      ? (props.employeeDraft || {}).isNonServiceUser
      : false,
  };

  return (
    <View flex="1">
      <div className="align-items-center d-flex">
        <div className="w-75">
          <EmployeeFilter
            employees={props.employees}
            options={props.clients}
            onSubmit={handleSubmit}
            resetButton={props.isFormSubmitted}
            onReset={handleSubmit}
            clientId={props.clientId}
            disabled={auditNeeded}
          />
        </div>
        <div className="w-25 d-flex justify-content-end">
          {props.canAddEmployee && (
            <Button onClick={props.onAddEmployeeHandler} disabled={auditNeeded}>
              {props.t("ADD_NEW_EMPLOYEE")}
            </Button>
          )}
        </div>
      </div>

      {auditNeeded && (
        <div>
          <p className="font-size-16 text-color-negative">{props.t("NOTIFICATION_AUDIT_DUE")}</p>
          <p className="font-weight-bold mb-2">{props.t("NOTIFICATION_AUDIT_DUE_TEXT_LINE1")}</p>
          <p className="font-weight-bold mt-2">{props.t("NOTIFICATION_AUDIT_DUE_TEXT_LINE2")}</p>

          <Button onClick={reviewMyEmployees}>{props.t("REVIEW_MY_EMPLOYEES")}</Button>

          <p className="font-size-16 text-color-negative mt-2">{props.t("IMPORTANT")}:</p>
          <p className="text-color-negative">{props.t("NOTIFICATION_AUDIT_DUE_TEXT_IMPORTANT")}</p>
        </div>
      )}

      {!auditNeeded && (
        <View minHeight="20rem" position="relative">
          <View>
            {props.t("SHOW_EMPLOYEE_RESULTS", {
              shownResults: props.shownResults,
              totalResults: props.totalResults,
            })}
          </View>

          {props.isLoading && <Loader inline />}
          <EmployeeList
            employees={props.employees}
            clientId={props.clientId}
            pagination={props.pagination}
            selectEmployeeLink={props.selectEmployeeLink}
            onSortChange={onSortChange}
            onPageChange={onPageChange}
          />
        </View>
      )}
      {props.canAddEmployee && props.isAddEmployeeModalVisible && (
        <AddEmployeeModal
          onDismissHandler={props.onDismissAddEmployeeHandler}
          onSubmit={onAddEmployee}
          schema={props.addEmployeeSchema}
          handleChange={handleChange}
          employee={employeeDraft}
        />
      )}
    </View>
  );
}

const mapStateToProps = (state: State, ownProps: OwnProps): PropsFromState => {
  const pagination = employeesSelectors.getEmployeesPagination(state);
  const pageRangeOffset = getPageRangeOffset(pagination);
  const totalResults = employeesSelectors.getTotalEmployees(state);
  const pageRange = getPageRange(pagination);
  let shownResults = `${pageRangeOffset} - ${pageRange}`;

  if (pageRange > totalResults) {
    shownResults = `${pageRangeOffset} - ${totalResults}`;
  }

  return {
    isLoading: employeesSelectors.isLoading(state),
    clients: employeesSelectors.getActiveClientsOptions(state),
    employees: employeesSelectors.getEmployees(state),
    totalResults,
    shownResults,
    error: employeesSelectors.getError(state),
    pagination,
    isFormSubmitted: hasSubmitSucceeded("employee-filter")(state),
    isAddEmployeeModalVisible: employeesSelectors.getIsAddEmployeeModalVisible(state),
    isAddEmployeeChooseClientModalVisible: employeesSelectors.getIsAddEmployeeChooseClientModalVisible(
      state,
    ),
    addEmployeeSchema: employeesSelectors.getAddEmployeeSchema(state),
    employeeDraft: employeesSelectors.getEmployeeDraft(state),
    employeesNotificationAuditList: getEmployeesNotificationAuditList(state),
    userId: getUserId(state),
    allowEmployeeAudit: getUserAllowEmployeeAudit(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  addEmployee: actionCreators.AddEmployee,
  getEmployees: actionCreators.GetEmployees,
  getAddEmployeeSchema: actionCreators.GetAddEmployeeSchema,
  onAddEmployeeHandler: () => actionCreators.ToggleAddEmployeeModal(true),
  onDismissAddEmployeeHandler: () => actionCreators.ToggleAddEmployeeModal(false),
  updateEmployeeDraft: actionCreators.UpdateEmployeeDraft,
  searchNotificationAuditByStatus: sharedActionCreators.SearchNotificationAuditByStatus,
  push,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(localize()(withFocusFirstInput(EmployeeSearch))),
);
